import * as React from "react";
import { InvitationStatus } from "src/common/event/event.types";
import { isDateBeforeToday, isDatesEqual } from "src/utils/dateUtils";

const formatDay = (date: Date): string =>
  date.toLocaleDateString("en-US", {
    weekday: "long",
  });

const renderDate = (date: Date): React.JSX.Element => (
  <div className="d-flex flex-column align-items-center gap-1">
    <div className="fs-14 fw-regular">{date.toLocaleString("en-US", { month: "short" }).toUpperCase()}</div>
    <h4 className="fw-bold">{date.toLocaleString("en-US", { day: "2-digit" })}</h4>
    <div className="fw-medium monospace-numbers">{date.toLocaleString("en-US", { year: "numeric" })}</div>
  </div>
);

interface EventDateDisplayProps {
  startDate: Date;
  endDate: Date;
  invitationStatus?: InvitationStatus;
}

const getEventDateClassNames = (
  endDate: Date,
  invitationStatus?: InvitationStatus
): { date: string; label: string } => {
  if (isDateBeforeToday(endDate)) {
    return { date: "bg-dark text-white", label: "text-secondary" };
  }

  switch (invitationStatus) {
    case InvitationStatus.Accepted:
      return { date: "bg-success text-white", label: "text-success" };
    case InvitationStatus.DeclinedNotAvailable:
    case InvitationStatus.DeclinedNotInterested:
      return { date: "bg-danger text-white", label: "text-danger" };
    case InvitationStatus.Pending:
      return { date: "bg-secondary-subtle text-body", label: "text-body" };
    default:
      return { date: "bg-secondary-subtle text-body", label: "text-body" };
  }
};

export const EventDateDisplay: React.FC<EventDateDisplayProps> = ({ startDate, endDate, invitationStatus }) => {
  const hasDifferentDate = !isDatesEqual(startDate, endDate);
  const eventDateClassName = getEventDateClassNames(endDate, invitationStatus);
  return (
    <div className="d-flex gap-2 align-items-center mt-n4 ms-n2">
      <div className={`d-flex gap-2 align-items-center px-2 py-3 ${eventDateClassName.date}`}>
        {hasDifferentDate ? (
          <>
            <div className="text-center">{renderDate(startDate)}</div>
            <h6>-</h6>
            <div className="text-center">{renderDate(endDate)}</div>
          </>
        ) : (
          <div className="text-center">{renderDate(startDate)}</div>
        )}
      </div>
      <div className={`d-flex flex-column ${eventDateClassName.label}`}>
        <div className="fs-18 fw-medium">
          {hasDifferentDate ? `${formatDay(startDate)} to ${formatDay(endDate)}` : formatDay(startDate)}
        </div>
      </div>
    </div>
  );
};
